import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Box,
  IconButton,
} from "@mui/material";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import { Game } from "../../../types/ScheduleTypes";
import { Club } from "../../../types/ClubTypes";

interface EditGameDialogProps {
  open: boolean;
  onClose: () => void;
  game: Game | null;
  clubs: Club[];
  onSave: (updatedGame: Game) => void;
  onDelete?: (gameId: number) => void;
}

export const EditGameDialog: React.FC<EditGameDialogProps> = ({
  open,
  onClose,
  game,
  clubs,
  onSave,
  onDelete,
}) => {
  const [formData, setFormData] = React.useState<Partial<Game>>({});

  React.useEffect(() => {
    if (game) {
      setFormData(game);
    } else {
      // Initialize with default values for new game
      setFormData({
        date: new Date().toISOString().split('T')[0],
        local_time: '',
        home_score: 0,
        away_score: 0,
        is_scrimmage: false,
      });
    }
  }, [game]);

  const handleChange = (field: keyof Game, value: any) => {
    setFormData((prev) => {
      // For club fields, ensure we have the complete club object
      if (field === 'home_club' || field === 'away_club') {
        const clubId = typeof value === 'object' ? value.id : value;
        const club = clubs.find(c => c.id === clubId);
        return {
          ...prev,
          [field]: club,
        };
      }
      return {
        ...prev,
        [field]: value,
      };
    });
  };

  const handleSwapTeams = () => {
    setFormData((prev) => {
      // Make sure we have complete club objects
      const homeClub = clubs.find(c => c.id === prev.home_club?.id) || prev.home_club;
      const awayClub = clubs.find(c => c.id === prev.away_club?.id) || prev.away_club;
      
      return {
        ...prev,
        home_club: awayClub,
        away_club: homeClub,
        home_score: prev.away_score,
        away_score: prev.home_score,
      };
    });
  };

  const handleSubmit = () => {
    if (formData) {
      if (game) {
        // Editing existing game
        onSave({
          ...game,
          ...formData,
        } as Game);
      } else {
        // Creating new game
        onSave(formData as Game);
      }
    }
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{game ? 'Edit Game' : 'New Game'}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start", mb: 2 }}>
          <TextField
            sx={{ width: "200px" }}
            label="Date"
            type="date"
            value={formData.date?.split("T")[0]}
            onChange={(e) => handleChange("date", e.target.value)}
            margin="normal"
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            sx={{ width: "200px" }}
            label="Time"
            type="time"
            value={formData.local_time || ""}
            onChange={(e) => handleChange("local_time", e.target.value)}
            margin="normal"
            InputLabelProps={{ shrink: true }}
            inputProps={{
              step: 300, // 5 min steps
            }}
          />
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <FormControl sx={{ width: "300px" }} margin="normal">
            <InputLabel>Home Club</InputLabel>
            <Select
              value={formData.home_club?.id || ""}
              onChange={(e) =>
                handleChange(
                  "home_club",
                  clubs.find((club) => club.id === e.target.value)
                )
              }
              label="Home Club"
            >
              {clubs.map((club) => (
                <MenuItem key={club.id} value={club.id}>
                  {club.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <IconButton
            onClick={handleSwapTeams}
            sx={{ mx: 1 }}
            title="Swap home and away teams"
          >
            <SwapHorizIcon />
          </IconButton>
          <FormControl sx={{ width: "300px" }} margin="normal">
            <InputLabel>Away Club</InputLabel>
            <Select
              value={formData.away_club?.id || ""}
              onChange={(e) =>
                handleChange(
                  "away_club",
                  clubs.find((club) => club.id === e.target.value)
                )
              }
              label="Away Club"
            >
              {clubs.map((club) => (
                <MenuItem key={club.id} value={club.id}>
                  {club.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 9,
            flexDirection: "row",
            justifyContent: "flex-start",
          }}
        >
          <TextField
            sx={{ width: "300px" }}
            label="Home Score"
            type="number"
            value={formData.home_score || ""}
            onChange={(e) =>
              handleChange("home_score", parseInt(e.target.value))
            }
            margin="normal"
          />
          <TextField
            sx={{ width: "300px" }}
            label="Away Score"
            type="number"
            value={formData.away_score || ""}
            onChange={(e) =>
              handleChange("away_score", parseInt(e.target.value))
            }
            margin="normal"
          />
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            marginTop: 2,
            marginLeft: 1,
          }}
        >
          <FormControlLabel
            control={
              <Switch
                checked={formData.is_scrimmage || false}
                onChange={(e) => handleChange("is_scrimmage", e.target.checked)}
              />
            }
            label="Is Scrimmage"
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
          <Box>
            {game && onDelete && (
              <Button 
                onClick={() => {
                  onDelete(game.id);
                  onClose();
                }} 
                color="error"
              >
                Delete
              </Button>
            )}
          </Box>
          <Box>
            <Button onClick={onClose} sx={{ mr: 1 }}>Cancel</Button>
            <Button onClick={handleSubmit} variant="contained" color="primary">
              Save
            </Button>
          </Box>
        </Box>
      </DialogActions>
    </Dialog>
  );
};
